import React, { useState } from 'react';
import { useStripe as useStripeJs, useElements } from '@stripe/react-stripe-js';
import { useStripe } from '../../contexts/StripeContext';
import { Loader2, AlertTriangle, DollarSign } from 'lucide-react';

const AchPaymentForm = ({ 
  amount, 
  eventId, 
  conventionOwnerId, 
  registrationData,
  onSuccess,
  onError,
  isDarkMode
}) => {
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [accountHolderName, setAccountHolderName] = useState('');
  const [email, setEmail] = useState('');
  const [bankSetupIntent, setBankSetupIntent] = useState(null);
  const [verificationMessage, setVerificationMessage] = useState('');
  
  // Get Stripe context and elements
  const { createPayment, stripeLoaded, error: stripeContextError } = useStripe();
  const stripeJs = useStripeJs();
  const elements = useElements();

  // Initialize ACH payment flow
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripeJs || !elements) {
      setError('Stripe payment system is not available at the moment. Please try again later.');
      return;
    }
    
    // Validate inputs
    if (!amount || !eventId || !conventionOwnerId) {
      setError('Missing required payment information');
      return;
    }

    if (!accountHolderName.trim()) {
      setError('Please enter the account holder name');
      return;
    }

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    try {
      setProcessing(true);
      setError(null);
      
      // Create a payment intent specifically for ACH
      const paymentIntent = await createPayment({
        amount: Math.round(amount * 100), // Convert to cents
        eventId,
        conventionOwnerId,
        registrationData: {
          ...registrationData,
          name: accountHolderName,
          email: email
        },
        paymentMethodType: 'us_bank_account' // Specify ACH payment method
      });
      
      if (!paymentIntent || !paymentIntent.clientSecret) {
        throw new Error('Failed to create payment intent');
      }
      
      // Collect bank account details
      const { error: collectError, setupIntent } = await stripeJs.collectBankAccountForPayment({
        clientSecret: paymentIntent.clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: {
              name: accountHolderName,
              email: email
            }
          }
        }
      });
      
      if (collectError) {
        throw new Error(collectError.message);
      }
      
      // Store the setup intent for verification
      setBankSetupIntent(setupIntent);
      
      // Set verification message
      setVerificationMessage('Please check your bank account for small verification deposits. This may take 1-2 business days.');
      
      // Payment will be completed after verification in a real implementation
      // For now, we'll simulate success
      setSucceeded(true);
      setProcessing(false);
      
      // Call success callback
      if (onSuccess) {
        onSuccess(paymentIntent);
      }
      
    } catch (err) {
      setError(err.message || 'An error occurred while processing your payment');
      setProcessing(false);
      
      // Call error callback
      if (onError) {
        onError(err);
      }
    }
  };

  // If Stripe failed to load entirely, show error message
  if (!stripeLoaded) {
    return (
      <div className={`max-w-md mx-auto p-6 border rounded-lg ${
        isDarkMode 
          ? 'border-red-800 bg-red-900/30 text-red-300' 
          : 'border-red-200 bg-red-50 text-red-700'
      }`}>
        <div className="flex items-center mb-4">
          <AlertTriangle className={`w-6 h-6 ${isDarkMode ? 'text-red-400' : 'text-red-600'} mr-2`} />
          <h3 className="text-lg font-medium">Payment Not Available</h3>
        </div>
        <p className="mb-3">
          The payment system is currently unavailable.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      <div className={`mb-4 p-4 rounded-lg ${
        isDarkMode 
          ? 'bg-blue-900/20 border border-blue-800/30' 
          : 'bg-blue-50 border border-blue-100'
      }`}>
        <div className="flex items-start">
          <DollarSign className={`w-5 h-5 mt-0.5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          <div className="ml-2">
            <h4 className={`font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
              Pay with ACH Direct Debit
            </h4>
            <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Connect your bank account to make a secure payment directly from your account. Lower fees help both you and the event organizer.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Display any context-level Stripe errors */}
        {stripeContextError && (
          <div className={`p-3 mb-4 rounded-md ${
            isDarkMode 
              ? 'bg-yellow-900/30 border border-yellow-800 text-yellow-300' 
              : 'bg-yellow-50 border border-yellow-200 text-yellow-700'
          }`}>
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />
              <span>{stripeContextError}</span>
            </div>
          </div>
        )}
      
        {/* Account holder name */}
        <div>
          <label className={`block text-sm font-medium mb-1 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Account Holder Name
          </label>
          <input
            type="text"
            value={accountHolderName}
            onChange={(e) => setAccountHolderName(e.target.value)}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
              isDarkMode 
                ? 'bg-gray-800 border-gray-700 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
            placeholder="Full name on account"
            required
          />
        </div>
        
        {/* Email */}
        <div>
          <label className={`block text-sm font-medium mb-1 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Email Address
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
              isDarkMode 
                ? 'bg-gray-800 border-gray-700 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
            placeholder="Your email address"
            required
          />
        </div>
        
        {/* Amount display */}
        <div className={`flex justify-between items-center p-4 rounded-lg ${
          isDarkMode ? 'bg-gray-800' : 'bg-gray-50'
        }`}>
          <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>Total Amount:</span>
          <span className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            ${(amount || 0).toFixed(2)}
          </span>
        </div>
        
        {/* Error message */}
        {error && (
          <div className={`p-3 rounded-md ${
            isDarkMode 
              ? 'bg-red-900/30 border border-red-800 text-red-300' 
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {error}
          </div>
        )}
        
        {/* Success/Verification message */}
        {(succeeded || verificationMessage) && (
          <div className={`p-3 rounded-md ${
            isDarkMode 
              ? 'bg-green-900/30 border border-green-800 text-green-300' 
              : 'bg-green-50 border border-green-200 text-green-700'
          }`}>
            {verificationMessage || 'Payment initiated successfully! ACH payments typically take 3-5 business days to process.'}
          </div>
        )}
        
        {/* Submit button */}
        <button
          type="submit"
          disabled={processing || succeeded || !stripeJs}
          className={`w-full py-3 px-4 rounded-md text-white font-medium transition-colors ${
            processing || succeeded || !stripeJs
              ? isDarkMode ? 'bg-gray-700 cursor-not-allowed' : 'bg-gray-400 cursor-not-allowed'
              : isDarkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {processing ? (
            <span className="flex items-center justify-center">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Processing...
            </span>
          ) : succeeded ? (
            'Payment Initiated'
          ) : (
            'Pay with Bank Account'
          )}
        </button>
      </form>
    </div>
  );
};

export default AchPaymentForm; 